import { useState, useEffect, useRef } from "react";
import { Flex, IconButton, Separator, Tooltip, Text, Box } from "@radix-ui/themes";
import { AvatarDropdownMenu, LayoutButton } from "components";
import { ReactComponent as BWifiIcon } from "../assets/icons/favicon-dark.svg";
import { BoltIcon, HomeIcon, WifiIcon, Cog6ToothIcon, StarIcon, MegaphoneIcon,
  ChevronDoubleRightIcon, ChevronDoubleLeftIcon, ClipboardDocumentListIcon } from "@heroicons/react/16/solid";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useProfileContext } from "context";
import HomeToolbar from "pages/home/HomeToolbar";

const TruncatedText = ({ children, size, weight, color }) => {
  const text = typeof children === 'string' ? children : String(children);
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef(null);
  
  useEffect(() => {
    const checkTruncation = () => {
      const container = containerRef.current;
      if (container) {
        setIsTruncated(container.scrollWidth > container.clientWidth);
      }
    };
    
    // Initial check
    checkTruncation();
    
    // Recheck on window resize
    window.addEventListener('resize', checkTruncation);
    
    return () => window.removeEventListener('resize', checkTruncation);
  }, [text]);
  
  const textElement = (
    <Box as="div"
      ref={containerRef} 
      style={{
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        verticalAlign: "center",
      }}
    >
      <Text size={size} weight={weight} color={color} width="100%">{text}</Text>
    </Box>
  );
  
  return isTruncated ? (
    <Tooltip content={text}>
      {textElement}
    </Tooltip>
  ) : textElement;
};

export function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useProfileContext();
  
  // Helper function to get initial selection from pathname
  const getInitialSelection = (pathname) => {
    const path = pathname.split("/")[1]; // Get first segment after the root
    if (path === "dashboard" || path === "home") return "home";
    if (path === "guest-wifi") return "guest-wifi";
    if (path === "presence") return "presence";
    if (path === "portal-selector" || path === "portal-brand" || path === "portal-location") return "guest-wifi";
    if (path === "networks" || path === "networks-selector") return "guest-wifi";
    if (path === "locations" || path === "contacts") return "guest-wifi";
    if (path === "reviews") return "reviews";
    if (path === "marketing") return "marketing";
    if (path === "surveys") return "surveys";
    return path || "home";
  };

  const [sidebarSelection, setSidebarSelection] = useState(getInitialSelection(location.pathname));
  const [isExpanded, setIsExpanded] = useState(false);

  // Update selection when route changes
  useEffect(() => {
    setSidebarSelection(getInitialSelection(location.pathname));
  }, [location.pathname]);

  const handleSidebarSelection = (flag) => {
    setSidebarSelection(flag);
    if (flag === "home") {
      navigate("/dashboard");
    } else if (flag === "presence") {
      navigate('/presence');
    } else if (flag === "guest-wifi") {
      navigate("/guest-wifi/captive-portal");
    } else if (flag === "reviews") {
      navigate("/reviews");
    } else if (flag === "marketing") {
      navigate("/marketing/analytics");
    } else if (flag === "surveys") {
      navigate("/surveys/analytics");
    } else {
      navigate(flag);
    }
  };

  // Helper component to render sidebar items cleanly
  const SidebarItem = ({ flag, label, icon: Icon, colorProp = "color" }) => {
    const isSelected = sidebarSelection === flag;
    
    // The button part (Icon)
    const buttonElement = (
      <LayoutButton flag={flag} state={sidebarSelection} onClick={handleSidebarSelection} label={label}>
        <Icon width="24" height="24" {...{ [colorProp]: isSelected ? "white" : "gray" }} />
      </LayoutButton>
    );

    if (!isExpanded) {
      // Collapsed: Icon only with Tooltip
      return (
        <Tooltip content={label} side="right" sideOffset={10}>
          <Flex>{buttonElement}</Flex>
        </Tooltip>
      );
    }

    // Expanded: Icon + Text in a Row
    return (
      <Flex align="center" gap="3" style={{ width: "100%", paddingLeft: "4px" }}>
        {buttonElement}
        <Text
          size="2"
          weight={isSelected ? "medium" : "regular"}
          color={isSelected ? "white" : "gray"}
          style={{ cursor: "pointer", whiteSpace: "nowrap" }}
          onClick={() => handleSidebarSelection(flag)}
        >
          {label}
        </Text>
      </Flex>
    );
  };

  return (
    <Flex className="sidebar-main-container" style={{ alignSelf: "stretch" }} height="100%">
      <Flex 
        className="sidebar-container" 
        style={{ 
          background: "var(--color-panel-solid)", 
          height: "100%", 
          alignSelf: "stretch",
          transition: "width 0.3s ease"
        }} 
        width={isExpanded ? "200px" : "64px"}
        px="2" 
        pb="5"
        pt="4"
        direction="column" 
        justify="between" 
        align={isExpanded ? "start" : "center"}
      >
        {/* Header / Logo Area */}
        <Flex direction={isExpanded ? "row" : "column"} align="center" justify="between" width="100%">
          <Flex height="40px" align="center" justify="center" pl={ isExpanded ? "8px" : "0px" }>
            <IconButton size="3" variant="ghost" aria-label="Go to Home" onClick={() => navigate("/dashboard")}>
              <BWifiIcon width="24" height="24" />
            </IconButton>
            <Flex direction="column" align="start"
              style={{
                marginLeft: isExpanded ? "8px" : "0px",
                opacity: isExpanded ? 1 : 0,
                maxWidth: isExpanded ? "120px" : "0px",
                overflow: "hidden",
                whiteSpace: "nowrap",
                transition: "all 0.3s ease"
              }}>
              <Text size="4" weight="bold">b connect</Text>
            </Flex>
          </Flex>
          
          {/* Toggle Button */}
          <Flex width="40px" height="40px" align="center" justify="center">
            <IconButton 
              size="3" 
              variant="ghost" 
              color="gray" 
              onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded 
              ? <Tooltip content="Collapse sidebar" side="right" sideOffset={10}>
                  <ChevronDoubleLeftIcon width={24} height={24} /> 
                </Tooltip>
              : <Tooltip content="Expand sidebar" side="right" sideOffset={10}>
                <ChevronDoubleRightIcon width={24} height={24} />
                </Tooltip>
              }
            </IconButton>
          </Flex>
        </Flex>

        {/* Sidebar section one */}
        <Flex className="sidebar-section-one" direction="column" align={isExpanded ? "start" : "center"} gap="2" width="100%">
          <SidebarItem flag="home" label="Home" icon={HomeIcon} />
          <SidebarItem flag="guest-wifi" label="Guest WiFi" icon={WifiIcon} />
          
          <SidebarItem flag="marketing" label="Marketing" icon={MegaphoneIcon} colorProp="fill" />
          <SidebarItem flag="surveys" label="Surveys" icon={ClipboardDocumentListIcon} />
          <SidebarItem flag="reviews" label="Reviews" icon={StarIcon} />
        </Flex>

        {/* Sidebar section two */}
        <Flex className="sidebar-section-two-container" direction="column" align={isExpanded ? "start" : "center"} 
          gap="4" width="100%">
          <Separator orientation="horizontal" size="4" style={{ minWidth: '1px', width: "100%" }} />
          
          <Flex className="sidebar-section-two" direction="column" align="center" gap="2" width="100%">
            <SidebarItem flag="integrations" label="Integrations" icon={BoltIcon} />
            <SidebarItem flag="organisation" label="Settings" icon={Cog6ToothIcon} />
          </Flex>
          
          <Flex width="100%" justify={isExpanded ? "start" : "center"} px="1" align="center">
            <AvatarDropdownMenu size="3" />
            <Flex direction="column" align="start" gap="0"
              style={{
                opacity: isExpanded ? 1 : 0,
                maxWidth: isExpanded ? "150px" : "0px",
                overflow: "hidden",
                whiteSpace: "nowrap",
                transition: "all 0.3s ease",
                marginLeft: isExpanded ? "12px" : "0px",
              }}>
              <TruncatedText size="2" weight="medium">
                {`${userProfile?.user_first_name || ''} ${userProfile?.user_last_name || ''}`.trim()}
              </TruncatedText>
              <TruncatedText size="1" color="gray">{userProfile?.user_email}</TruncatedText>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      
      <Separator orientation="vertical" size="4" style={{ minWidth: '1px' }} />
      
      <Flex direction="column" style={{ alignSelf: "stretch", flex: "1 0 0", height: "100%", overflow: "hidden", minWidth: 0 }}>
        <HomeToolbar />
        <Flex style={{ alignSelf: "stretch", flex: "1 1 auto", alignItems: "flex-start", minHeight: 0, overflow: "hidden", minWidth: 0 }}>
          {/* This is where the routed components will be rendered */}
          <Outlet />
        </Flex>
      </Flex>
    </Flex>
  );
}
